import axios from 'axios'
import hash from 'object-hash'

import { Palette } from '../'
import { mergeDeepAll } from '../utils'
import {
  CompassAPIMultiSeriesChartData,
  convertCompassToHighchartsDataFormat,
  dataLabelsFormatter,
  getCompassAPIChartData,
  getDataSources,
  getMultipleColumnChartProps,
  getMultipleLineChartProps,
  lastPointLabelFormatter,
  magnitudePointFormatter,
  tooltipFormatterScatter,
} from '../utils/compass'
import { range6m1yAll } from '../utils/config'
import { formatSeriesNames } from '../utils/string'
import type { BaseContext } from './'
import {
  CommonChartConfig,
  commonChartOptions,
  commonDispersionChartOptions,
} from './common'

export interface CompassAPIScatterChartData {
  data: {
    eps_growth_2022: number
    'p/e_1bf': number
    ticker: string
  }[]
}

const EXCHANGE_CHARTS_PATH = '/api/charts/exchange'

export namespace Exchange {
  export namespace WorldBank {
    const CONTEXT_ID = 'equity:exchange:world_bank'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'penetration' | 'turnover' | 'market_listed_domestics'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) =>
        getMultipleLineChartProps(
          Context,
          {
            addMinMaxIndicators: true,
            convertToPercentages: Array(data.data.length).fill(true),
            tooltip: { xDateFormat: '%Y' },
          },
          {
            type: Array(data.data.length).fill('spline'),
          }
        )(config, data),
    }
  }

  export namespace B3Operational {
    const CONTEXT_ID = 'equity:exchange:b3_operational'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'adtv'
        | 'participation_volume'
        | 'average_depository'
        | 'average_depository_share'
        | 'individuals_accounts'

      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'adtv':
          case 'individuals_accounts':
            return getLineChartProps(config, data)
          case 'participation_volume':
          case 'average_depository_share':
            return getColumnChartProps(config, data)
          case 'average_depository':
            return getAverageDepositoryChartProps(config, data)
        }
      },
    }

    const getLineChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          tooltip: {
            pointFormatter: magnitudePointFormatter(),
            xDateFormat: '%B %Y',
          },
        },
        {
          ...(config.chart == 'individuals_accounts' ? { name: [''] } : {}),
        }
      )(config, data)

    const getColumnChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleColumnChartProps(Context, {
        convertToPercentages: [true, true, true, true],
        plotOptions: {
          column: {
            stacking: 'percent',
          },
        },
        tooltip: {
          xDateFormat: '%m/%Y',
        },
        xAxis: {
          labels: {
            format: '{value:%m/%Y}',
            rotation: -45,
          },
          tickPositions: data.data[0].data.map((point) =>
            new Date(point.date).getTime()
          ),
        },
      })(config, data)

    const getAverageDepositoryChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleColumnChartProps(Context, {
        plotOptions: {
          column: {
            stacking: 'normal',
          },
        },
        tooltip: {
          pointFormatter: magnitudePointFormatter(),
          xDateFormat: '%m/%Y',
        },
        xAxis: {
          labels: {
            format: '{value:%m/%Y}',
            rotation: -45,
          },
          tickPositions: data.data[0].data.map((point) =>
            new Date(point.date).getTime()
          ),
        },
      })(config, data)
  }

  export namespace PublicOffers {
    const CONTEXT_ID = 'equity:exchange:public_offers'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'participation_series'
        | 'participation_series_share'
        | 'market_value_share'
        | 'brazil_ipos_rolling'
        | 'brazil_ipos_rolling_over_gdp'
        | 'us_ipos_rolling'
        | 'us_ipos_rolling_over_gdp'
        | 'ipo_and_follow_on'
        | 'dividends_and_buyback'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'participation_series':
            return getParticipationSeriesChartProps(config, data)
          case 'market_value_share':
            return getMarketValueShareChartProps(config, data)
          case 'participation_series_share':
            return getStackedPercentColumnChartProps(config, data)
          case 'brazil_ipos_rolling':
          case 'us_ipos_rolling':
            return getStackedMixedChartProps(config, data)
          case 'brazil_ipos_rolling_over_gdp':
          case 'us_ipos_rolling_over_gdp':
            return getLineChartProps(config, data)
          case 'ipo_and_follow_on':
          case 'dividends_and_buyback':
            return getMultiColumnsChartProps(config, data)
        }
      },
    }

    const getParticipationSeriesChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleColumnChartProps(Context, {
        plotOptions: {
          column: {
            stacking: 'normal',
          },
        },
        tooltip: {
          pointFormatter: magnitudePointFormatter(),
          xDateFormat: '%m/%Y',
        },
        xAxis: {
          labels: {
            format: '{value:%m/%Y}',
          },
          tickPositions: data.data[0].data.map((point) =>
            new Date(point.date).getTime()
          ),
        },
      })(config, data)

    const getMarketValueShareChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleColumnChartProps(Context, {
        convertToPercentages: [true],
        plotOptions: {
          column: {
            stacking: 'normal',
          },
        },
        tooltip: {
          xDateFormat: '%m/%Y',
        },
        xAxis: {
          labels: {
            format: '{value:%m/%Y}',
            rotation: -45,
          },
          tickPositions: data.data[0].data.map((point) =>
            new Date(point.date).getTime()
          ),
        },
      })(config, data)

    const getStackedPercentColumnChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleColumnChartProps(Context, {
        convertToPercentages: [true, true, true, true],
        plotOptions: {
          column: {
            stacking: 'percent',
          },
        },
        tooltip: {
          xDateFormat: '%m/%Y',
        },
        xAxis: {
          labels: {
            format: '{value:%m/%Y}',
          },
          tickPositions: data.data[0].data.map((point) =>
            new Date(point.date).getTime()
          ),
        },
      })(config, data)

    const getStackedMixedChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleColumnChartProps(
        Context,
        {
          convertToPercentages: [false, false, true],
          plotOptions: {
            column: {
              stacking: 'normal',
            },
          },
          tooltip: {
            xDateFormat: '%m/%Y',
          },
          xAxis: {
            labels: {
              format: '{value:%m/%Y}',
              rotation: -45,
            },
            tickPositions: data.data[0].data.map((point) =>
              new Date(point.date).getTime()
            ),
          },
          yAxis: [{ id: 'absolute' }, { id: 'percentual', opposite: true }],
        },
        {
          type: ['column', 'column', 'spline'],
          yAxis: ['absolute', 'absolute', 'percentual'],
          tooltip: Array(2).fill({
            pointFormatter: magnitudePointFormatter(),
          }),
        }
      )(config, data)

    const getLineChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(Context, {
        convertToPercentages: [true, true],
      })(config, data)

    const getMultiColumnsChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleColumnChartProps(
        Context,
        {
          chartTitle: config.title,
          plotOptions: { column: { stacking: 'normal' } },
          tooltip: {
            pointFormatter: magnitudePointFormatter(),
            xDateFormat: '%B %Y',
          },
        },
        {
          name:
            config.chart === 'ipo_and_follow_on'
              ? ['IPO', 'Follow-on']
              : config.chart === 'dividends_and_buyback'
              ? ['Buyback', 'Dividends projection', 'Dividends']
              : [''],
        }
      )(config, data)
  }

  export namespace MarketCap {
    const CONTEXT_ID = 'equity:exchange:market_cap'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'market_cap_gdp_us'
        | 'market_cap_m1_us'
        | 'market_cap_us_mkt_cap_world'
        | 'market_cap_equities_gdp'
        | 'market_cap_equities_mkt_cap_world'

      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'market_cap_gdp_us':
          case 'market_cap_m1_us':
          case 'market_cap_us_mkt_cap_world':
            return getLineChartProps(config, data)
          case 'market_cap_equities_gdp':
          case 'market_cap_equities_mkt_cap_world':
            return getMktCapEquitiesChartProps(config, data)
        }
      },
    }

    const getLineChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(Context, {
        addMinMaxIndicators: true,
        convertToPercentages: [true],
        tooltip: { xDateFormat: '%B %Y' },
      })(config, data)

    const minMaxLabel = function (this: {
      series: { dataMax: number; dataMin: number }
      y: number
    }) {
      return this.y === this.series.dataMin || this.y === this.series.dataMax
        ? this.y.toFixed(2) + '%'
        : null
    }

    const getMktCapEquitiesChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) => {
      const series = data.data.map((s, idx) => ({
        data: convertCompassToHighchartsDataFormat(s.data, {
          convertPercentage: true,
        }),
        color:
          idx === 0
            ? Palette.darkBlue
            : idx === 1
            ? Palette.darkGreen
            : idx === 2
            ? Palette.fadedBlue
            : Palette.green,
        dashStyle: idx < 2 ? 'Solid' : 'LongDash',
        dataLabels: [
          {
            enabled: true,
            formatter:
              idx < 2 ? minMaxLabel : lastPointLabelFormatter({ unit: '%' }),
          },
        ],
        id: `${config.chart}-${idx}`,
        name: s.name,
        showInLegend: idx < 2,
        tooltip: {
          valueDecimals: 2,
          valueSuffix: '%',
          xDateFormat: '%B %Y',
        },
        type: 'line',
        yAxis: 'default',
      }))

      const yAxis = [
        {
          id: 'default',
          opposite: false,
          title: { text: '' },
          labels: { format: '{value}%' },
        },
      ]

      return {
        id: Context.getChartId(config),
        title: data.title,
        options: mergeDeepAll([
          getDataSources(data.data_sources),
          commonChartOptions,
          {
            rangeSelector: range6m1yAll,
            series,
            yAxis,
          },
        ]),
        stockTools: true,
      }
    }
  }

  export namespace Utvm {
    const CONTEXT_ID = 'equity:exchange:utvm'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'volume' | 'quantity'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) =>
        getMultipleLineChartProps(Context, {
          addMinMaxIndicators: true,
          tooltip: {
            pointFormatter: magnitudePointFormatter(),
            xDateFormat: '%B %Y',
          },
        })(config, data),
    }
  }

  export namespace Ufin {
    const CONTEXT_ID = 'equity:exchange:ufin'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'financing' | 'financing_share'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) =>
        getMultipleLineChartProps(Context, {
          addMinMaxIndicators: true,
          ...(config.chart === 'financing_share'
            ? { convertToPercentages: Array(data.data.length).fill(true) }
            : {}),
          tooltip: {
            pointFormatter: magnitudePointFormatter({
              unit: config.chart === 'financing_share' ? '%' : '',
            }),
            xDateFormat: '%B %Y',
          },
        })(config, data),
    }
  }

  export namespace RvRf {
    type RvRfChartId = 'renda_fixa_e_hibridos' | 'renda_variavel'

    const RV_RF_CHART_PATHS: Record<RvRfChartId, string> = {
      renda_fixa_e_hibridos: '/rv-rf/renda-fixa-e-hibridos',
      renda_variavel: '/rv-rf/renda-variavel',
    }

    const CONTEXT_ID = 'equity:exchange:rv_rf'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      context: typeof CONTEXT_ID
      chart: RvRfChartId
      title?: string
      emissionType?: string
      instituicoes?: string[]
      coordenadores?: string[]
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: EXCHANGE_CHARTS_PATH + RV_RF_CHART_PATHS[config.chart],
        method: 'POST',
        data: {
          ...(config.emissionType ? { orig_or_dist: config.emissionType } : {}),
          ...(config.instituicoes ? { instituicoes: config.instituicoes } : {}),
          ...(config.coordenadores
            ? { coordenadores: config.coordenadores }
            : {}),
        },
      }).then((response) => response.data)

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          tooltip: {
            pointFormatter: magnitudePointFormatter(),
            xDateFormat: '%B %Y',
          },
          xAxis: { labels: { format: '{value:%Y}' } },
        },
        { type: Array(data.data.length).fill('spline') }
      )(config, formatSeriesNames(data))

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.chart}:${hash(config, {
          unorderedArrays: true,
        })}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace Valuation {
    const CONTEXT_ID = 'equity:exchange:valuation'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'pe_bolsaa_mm'
        | 'pe_b3sa3_bs'
        | 'pe_ice_us'
        | 'pe_cme_us'
        | 'pe_ndaq_us'
        | 'pe_lseg_ln'
        | 'pe_sgx_au'
        | 'pe_asx_au'
        | 'pe_388_hk'
        | 'pe_db1_gy'
        | 'scatter'
        | 'ratio_pe_b3sa3_peers'

      context: typeof CONTEXT_ID
    }

    export type ChartData =
      | CompassAPIMultiSeriesChartData
      | CompassAPIScatterChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'pe_bolsaa_mm':
          case 'pe_b3sa3_bs':
          case 'pe_ice_us':
          case 'pe_cme_us':
          case 'pe_ndaq_us':
          case 'pe_lseg_ln':
          case 'pe_sgx_au':
          case 'pe_asx_au':
          case 'pe_388_hk':
          case 'pe_db1_gy':
          case 'ratio_pe_b3sa3_peers':
            return getMultipleChartProps(
              config,
              data as CompassAPIMultiSeriesChartData
            )
          case 'scatter':
            return getScatterChartProps(
              config,
              data as CompassAPIScatterChartData
            )
        }
      },
    }

    const getMultipleChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          ...(config.chart === 'ratio_pe_b3sa3_peers'
            ? {
                chartTitle:
                  'Ratio PE F12m (B3SA3 / ASX, 388, LSEG, CME, SGX, DB1, ICE, B3SA3)',
              }
            : {}),
          msdIndicators: { avg: true, sd1: true, sd2: true },
        },
        { name: [''] }
      )(config, data)

    const getScatterChartProps = (
      config: ChartConfig,
      data: CompassAPIScatterChartData
    ) => {
      const series = data.data.map((point) => ({
        animation: false,
        data: [
          {
            x: point.eps_growth_2022 * 100,
            y: point['p/e_1bf'],
            name: point.ticker,
          },
        ],
        dataLabels: {
          allowOverlap: false,
          enabled: true,
          formatter: dataLabelsFormatter,
          useHtml: true,
          zIndex: 0,
        },
        label: point.ticker,
        name: point.ticker,
        showInLegend: false,
      }))

      const tooltip = {
        backgroundColor: 'rgba(247,247,247,0.85)',
        enabled: true,
        formatter: tooltipFormatterScatter({
          xAxisSuffix: '%',
          xAxisLabel: 'EPS Growth 2022',
          yAxisLabel: 'PE',
        }),
        shared: false,
        style: {
          color: '#333333',
          cursor: 'default',
          fontSize: '12px',
          whiteSpace: 'nowrap',
        },
        useHTML: true,
        zIndex: 10,
      }

      return {
        id: Context.getChartId(config),
        title: 'PE versus EPS Growth 2022',
        options: {
          ...commonChartOptions,
          ...commonDispersionChartOptions,
          series,
          tooltip,
          xAxis: {
            title: { text: 'EPS Growth 2022' },
            labels: { format: '{value}%' },
          },
          yAxis: {
            title: { text: 'PE' },
            labels: { format: '{value}' },
          },
        },
        stockTools: false,
        useRegularChart: true,
      }
    }
  }
}
